import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getHome, type ArchiveHome } from "../api";
import { ErrorState, LoadingState } from "../components/PageState";
import { selectFeatured } from "../featured";
import { assignArtwork } from "../artwork";
import { AppInvitation } from "../components/AppInvitation";
import { WorkCard } from "../components/WorkCard";
import { useLibrary } from "../contexts/LibraryContext";
import { usePageMeta } from "../hooks/usePageMeta";
import { browseArtwork } from "../artwork";
import { Icon } from "../components/Icon";
export function HomePage() {
  const [home, setHome] = useState<ArchiveHome | null>(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(false);
  const featured = selectFeatured(home?.shelves || []);
  const artwork = assignArtwork(featured, "home:featured");
  const navigate = useNavigate();
  const library = useLibrary();
  usePageMeta(
    "Eternally Blessed Library",
    "Christian teachings, books, music and Scripture studies. Free to explore.",
    "/",
  );
  useEffect(() => {
    getHome()
      .then(setHome)
      .catch(() =>
        setError(
          "The library could not be loaded. Please refresh to try again.",
        ),
      );
  }, []);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    navigate(`/search?q=${encodeURIComponent(query.trim())}`);
  };
  return (
    <div className="home-page">
      <section className="welcome">
        <div className="welcome-copy">
          <h1>
            Good words.
            <br />
            <em>For a full life.</em>
          </h1>
          <p>Christian teachings, books, and music. Freely shared.</p>
          <form onSubmit={submit} role="search">
            <input
              aria-label="Search teachings and written text"
              placeholder="Search the library"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button>Search</button>
          </form>
          <div className="welcome-prompts">
            Try <Link to="/search?q=prayer">prayer</Link>
            <Link to="/search?book=Romans&chapter=8">Romans 8</Link>
            <Link to="/search?q=renewed%20mind">renewed mind</Link>
          </div>
        </div>
        <div className="welcome-art" aria-hidden="true">
          <img src={browseArtwork("hope")} alt="" />
          <span>Until his return.</span>
        </div>
      </section>
      {library.history.some(
        (w) => library.progress[w.id] > 0 && !library.completed.includes(w.id),
      ) ? (
        <section className="continue-section">
          <div className="section-kicker">PICK UP WHERE YOU LEFT OFF</div>
          <h2>Continue listening or reading</h2>
          {library.history
            .filter(
              (w) =>
                library.progress[w.id] > 0 && !library.completed.includes(w.id),
            )
            .slice(0, 2)
            .map((w) => (
              <WorkCard key={w.id} work={w} compact />
            ))}
        </section>
      ) : null}
      {error ? (
        <ErrorState message={error} />
      ) : !home ? (
        <LoadingState label="Opening the library…" />
      ) : (
        <section className="featured-section" aria-labelledby="featured-title">
          <div className="section-heading">
            <div>
              <div className="section-kicker">A LITTLE SOMETHING TO BEGIN WITH</div>
              <h2 id="featured-title">From the library.</h2>
            </div>
            <Link className="text-link" to="/search">Browse all <Icon name="arrow" /></Link>
          </div>
          <div className="featured-grid" aria-label="Featured content" tabIndex={0}>
            {featured.slice(0, expanded ? 12 : 6).map((work, index) => (
              <WorkCard key={work.id} work={work} featured artwork={artwork[index]} />
            ))}
          </div>
          {featured.length > 6 && !expanded ? (
            <button className="featured-more" onClick={() => setExpanded(true)}>Show more selections <span aria-hidden="true">+</span></button>
          ) : null}
          {!featured.length && <p>There is more to discover. <Link to="/search">Browse the library</Link>.</p>}
        </section>
      )}
      <AppInvitation />
      <section className="library-mission" aria-labelledby="mission-title">
        <div className="section-kicker">ABOUT ETERNALLY BLESSED</div>
        <h2 id="mission-title">Preserved with care.<br /><em>Shared with you.</em></h2>
        <p>We preserve and share Christian research, teachings, and music for the benefit of the body of Christ—and for anyone who wants to get to know God through the Bible.</p>
        <a className="text-link" href="https://eternallyblessed-site.pages.dev">More about Eternally Blessed <Icon name="arrow" /></a>
      </section>
    </div>
  );
}
